import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Write a Prayer Letter People Actually Read';
const DESCRIPTION =
    'Seven writing moves that turn a prayer letter donors skim into one they finish, remember, and reply to — plus what to do about the money ask.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover5.jpg',
            datePublished: '2026-07-20',
            dateModified: '2026-07-20',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-write-a-prayer-letter-people-actually-read',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How long should a prayer letter be?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Short enough that a tired reader finishes it on their phone — one scene, one named person, one ask, one closing line. Length matters less than whether every sentence earns its place; cut the draft by a third before you send it.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How often should I send a prayer letter?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Monthly is the standard cadence for staying in front of supporters without becoming background noise. Reserve a dedicated support ask for once or twice a year — most months should just be the story and the one small ask, not a funding pitch.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I include a financial ask in every letter?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'No. A letter that occasionally names a real, specific funding need outperforms one that quietly angles for support every month — donors tune out a constant soft ask faster than an honest, infrequent direct one.',
                    },
                },
            ],
        },
    ],
};

export default function HowToWriteAPrayerLetterPeopleActuallyRead() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-write-a-prayer-letter-people-actually-read' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-prayer-letter-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover5.jpg" alt="A handwritten letter and pen on a wooden table" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>If your last prayer letter went out and you got two replies — both from your mom — this post is for you.</p>

                    <p>There are letters donors quote back to you a year later, and letters donors quietly archive after the first sentence. The two aren't separated by talent, spiritual maturity, or how much is happening on the field. They're separated by a small set of writing moves that almost nobody teaches you in pre-field training. Here are the ones that matter.</p>

                    <h2>First, what a prayer letter is <em>not</em></h2>

                    <p>It is not a report. It is not a sermon. It is not a marketing email. It is a letter from a specific person, in a specific place, to a specific reader, about something specific that happened. Every move below is about getting closer to that.</p>

                    <p>If you write your next letter while picturing one real supporter — not your whole list — you'll already be ahead of most missionaries writing online. Pick someone. Picture their face. Write to them.</p>

                    <h2>Seven writing moves that change everything</h2>

                    <h3>1. Open with a noun, not a feeling</h3>

                    <p>The most common opening sentence on the field is some version of "We are so blessed and grateful for your continued partnership in the gospel." It's true. It's also instantly skippable — the reader has read four hundred sentences like it and their brain has learned to slide past.</p>

                    <p>The fix: open with a concrete noun the reader can see.</p>

                    <blockquote><p>The fan in the language school classroom broke on Tuesday and now we're all studying Thai at thirty-four degrees.</p></blockquote>

                    <p>That sentence isn't better because it's clever. It's better because it's a thing. A reader can picture it. Once they can picture something, they keep reading.</p>

                    <h3>2. Use one scene, not a summary of the month</h3>

                    <p>A summary of the month is a list. A scene is a moment. Lists don't move people; moments do.</p>

                    <p><strong>Bad:</strong> "This month we have been busy with language study, ministry meetings, and home schooling, and we are seeing God move in many ways."</p>

                    <p><strong>Better:</strong> "On Wednesday, Boon came back to the Bible study he had walked out of two weeks earlier. He sat down, looked at the floor, and asked if we could start over from chapter one."</p>

                    <p>You don't need to write the whole month. You need to write one Wednesday.</p>

                    <h3>3. Name names, especially small ones</h3>

                    <p>A prayer letter where everyone is "the team," "the kids," or "a local friend" reads like fiction. A prayer letter where Boon is Boon and Naa is Naa reads like a place that exists.</p>

                    <p>Yes, you sometimes have to anonymize for safety. When you do, say so plainly: "I'm changing the name for safety reasons — let's call him Boon." Readers respect that more than the blurred-out version.</p>

                    <h3>4. Cut the inherited church language</h3>

                    <p>There's a register of words that crept into missionary writing decades ago and never quite left: <em>blessed, anointed, mighty move, the Lord laid on my heart, doors are opening, the harvest is plentiful.</em> They aren't wrong. They're just so worn that they've lost the ability to make anyone see anything.</p>

                    <p>A useful test: read your draft out loud and circle every phrase you'd never say to a non-Christian friend over coffee. Replace it with what you'd actually say at the coffee. You'll lose some piety. You'll gain readers.</p>

                    <h3>5. Have one ask. Make it small enough to answer</h3>

                    <p>The temptation every month is to bundle: pray for the work, pray for the team, pray for the family, pray for the budget. Each is real, but four asks land as zero asks — the reader doesn't know which to hold.</p>

                    <p>One ask, named, this month: "Will you pray for Naa this week? She's the woman in the story above. She comes back every Wednesday now, and the question she keeps asking is whether God knows her dead son's name."</p>

                    <p>That's a prayer a reader can pray on the walk to their car.</p>

                    <h3>6. End like a friend, not like a pastor</h3>

                    <p>The closing line is where most letters fall off a cliff: "Yours in His service," "Pressing on for the Kingdom," "Grace and peace." Not bad — just indistinguishable from every other missionary letter the reader has ever gotten.</p>

                    <p>A closing line that sounds like <em>you</em>: "Thanks for reading. If you've got a minute this week, pray for Naa." Or: "We'll write again next month. Tell us how your spring is going — we miss it." The reader's brain pays the most attention to first and last sentences. Spend the writing budget there.</p>

                    <h3>7. Cut the letter by a third</h3>

                    <p>Almost every draft is 30% too long. You wrote it; you can feel which third. Cut it. The remaining two-thirds will be the strongest version of the letter, and the reader will finish it. This is the one habit the missionaries who write the most-read letters all share: they're ruthless editors of their own first drafts.</p>

                    <h2>What to do about the asks for money</h2>

                    <p>The honest answer most pre-field training won't give you: a recurring monthly letter isn't the right place to make a <em>new</em> support ask. A letter that mostly tells a story, and occasionally — once or twice a year — names a real funding need, outperforms a letter that gently angles for support every month. Donors stop reading the latter within six months.</p>

                    <p>When you do have a real ask, name it plainly, with a number, with a deadline, without rhetorical framing: "We're $400/month short of full support. We're trying to close that by August. If recurring monthly giving fits your life, here is the page." That sentence has done more work for the missionaries I know than any "open letter" ever has.</p>

                    <h2>A small word about the tax-deductibility question</h2>

                    <p>You'll get asked. Here's the most useful thing to say: "Most months are not about tax deductions. They are about partnership." For donors who do need to itemize larger gifts, point them at whatever fiscal-sponsor or 501(c)(3) channel you actually have. Treat it as logistics, not a sales objection. (More on the options here: <a href="/resources/how-to-start-monthly-giving-page-without-501c3">how to start a monthly giving page without becoming a 501(c)(3)</a>.)</p>

                    <h2>Quick checklist before you hit send</h2>

                    <p>Read your draft out loud and ask:</p>
                    <ul>
                        <li>Can the reader picture the first sentence? If no, rewrite it as a noun.</li>
                        <li>Is there one scene with at least one named person? If no, add one.</li>
                        <li>Are there more than two of the worn-church phrases on the list above? If yes, cut.</li>
                        <li>Is there exactly one ask, sized small enough to act on this week? If not, narrow.</li>
                        <li>Does the closing line sound like the writer or like a denominational form letter? If the latter, rewrite.</li>
                        <li>Is the letter shorter than it was twenty minutes ago? If not, cut a third.</li>
                    </ul>

                    <p>If you can answer yes to all six, send it. The version you sent is better than the polished version you didn't.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How long should a prayer letter be?</strong><br />
                    Short enough that a tired reader finishes it on their phone — one scene, one named person, one ask, one closing line. Length matters less than whether every sentence earns its place; cut the draft by a third before you send it.</p>

                    <p><strong>How often should I send a prayer letter?</strong><br />
                    Monthly is the standard cadence for staying in front of supporters without becoming background noise. Reserve a dedicated support <em>ask</em> for once or twice a year — most months should just be the story and the one small ask, not a funding pitch.</p>

                    <p><strong>Should I include a financial ask in every letter?</strong><br />
                    No. A letter that occasionally names a real, specific funding need outperforms one that quietly angles for support every month — donors tune out a constant soft ask faster than an honest, infrequent direct one.</p>

                    <h2>Next reads</h2>
                    <ul>
                        <li><a href="/resources/how-to-start-monthly-giving-page-without-501c3">How to Start a Monthly Giving Page as a Missionary (Without Becoming a 501(c)(3))</a> — the practical follow-up question many of these letters raise.</li>
                        <li><a href="/resources/true-cost-missionary-fundraising-platforms-2026">The True Cost of Every Missionary Fundraising Platform in 2026</a> — an honest, all-in fee comparison if you're still choosing where to host your giving page.</li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>, a recurring-giving platform built for support-raised missionaries. We read a lot of prayer letters as part of the day job and wrote this after noticing the same seven moves in every letter that actually got read.</p>
                    <p><small><em>Last updated: 2026-07-20.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-write-a-prayer-letter-people-actually-read" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
